import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NetstaffLogo } from "@/components/NetstaffLogo";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}`);
      if (user.role === "facility") navigate("/facility");
      else if (user.role === "professional") navigate("/pro");
      else navigate("/dispatch");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const quickFill = (em, pw) => {
    setEmail(em);
    setPassword(pw);
  };

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center p-6">
      <div className="w-full max-w-5xl grid md:grid-cols-2 gap-8 items-center">
        <div className="hidden md:block">
          <Link to="/"><NetstaffLogo textClass="text-3xl" className="h-14 w-14" /></Link>
          <h1 className="mt-12 text-4xl font-bold text-brand-navy leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Welcome back.<br />
            <span className="text-brand-teal">Let's get to work.</span>
          </h1>
          <p className="mt-4 text-slate-600">Sign in to manage requests, dispatch shifts, and track your team.</p>
          <div className="mt-10 p-6 rounded-2xl bg-white border border-slate-200">
            <div className="text-xs uppercase tracking-wider text-brand-teal font-semibold mb-3">Demo accounts</div>
            <div className="space-y-2 text-sm">
              {[
                { label: "Super Admin", testKey: "super-admin", email: "admin@netstaff.ca", pw: "NetStaff@2026" },
                { label: "Dispatcher", testKey: "dispatcher", email: "dispatcher@netstaff.ca", pw: "Dispatch@2026" },
                { label: "Facility", testKey: "facility", email: "facility1@netstaff.ca", pw: "Facility@2026" },
                { label: "Professional RN", testKey: "professional-rn", email: "emily.johnson@netstaff.ca", pw: "Pro@2026" },
              ].map((d) => (
                <button
                  key={d.email}
                  type="button"
                  onClick={() => quickFill(d.email, d.pw)}
                  className="w-full text-left flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50 group"
                  data-testid={`demo-${d.testKey}`}
                >
                  <div>
                    <div className="font-medium text-slate-900">{d.label}</div>
                    <div className="text-xs text-slate-500">{d.email}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-brand-teal" />
                </button>
              ))}
            </div>
          </div>
        </div>

        <Card className="border-slate-200 shadow-xl shadow-brand-navy/5">
          <CardHeader>
            <div className="md:hidden mb-4"><NetstaffLogo /></div>
            <CardTitle className="text-2xl text-brand-navy" style={{ fontFamily: "Outfit, sans-serif" }}>Sign in</CardTitle>
            <p className="text-sm text-slate-500">Access your NetStaff dashboard</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="login-email-input" />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password-input" />
              </div>
              {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="login-error">{error}</div>}
              <Button type="submit" className="w-full bg-brand-navy hover:bg-brand-blue h-11" disabled={loading} data-testid="login-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
              </Button>
            </form>
            <div className="mt-6 text-sm text-center text-slate-500">
              New to NetStaff?{" "}
              <Link to="/register" className="text-brand-teal font-semibold hover:underline" data-testid="login-to-register-link">Create account</Link>
            </div>
            <div className="mt-2 text-sm text-center text-slate-500">
              <Link to="/" className="hover:underline" data-testid="login-back-home">← Back to home</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
